import '../styles/Map_Page.css';
import React, {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {usePeople, useReunions} from '../hooks/hooks_index';
import {Loading} from "../components/components_index";
import Map, {Marker,Popup} from "react-map-gl/mapbox";
import 'mapbox-gl/dist/mapbox-gl.css';

const token = "pk.eyJ1IjoianNwcmFkbGV5MDMiLCJhIjoiY21od2tod2I0MDBqdjJsb3U5dnhyZzBzeiJ9.mvEEnbzWVmP7S6FUVoyhqA";
// GO TO chrome://flags/ IF GETTING CONSOLE ERROR AND MAP IS BLANK

export default function MapPage({children}) {
  const initialState = {
    longitude: -96,
    latitude: 38.5,
    zoom: 3.6
  }
  const [mapState, setMapState] = useState (initialState);
  const [mapLoading, setMapLoading] = useState(true);

  const [popupInfo, setPopupInfo] = useState(null);
  const [peopleLocations, setPeopleLocations] = useState(null);
  const [reunionLocations, setReunionLocations] = useState(null);

  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: reunions, isLoading: reunionsLoading, error: reunionsError} = useReunions();

  const peopleList = useMemo(() => (people ? Object.entries(people) : []), [people]);
  const reunionList = useMemo(() => (reunions ? Object.entries(reunions) : []), [reunions]);

  const [p_isChecked, setP] = useState(true);
  const [r_isChecked, setR] = useState(true);

  const [fullScreen, setFullScreen] = useState(false);

  const mapRef = useRef(null);
  const cleanupWheelRef = useRef(null);

  useEffect(() => {
    if (!people) return;
    async function fetchLocations() {
      const locations = await Promise.all(
        peopleList
          .filter(([id, person]) => person.address && !id.startsWith("P0"))
          .map(async ([id, person]) => {
            const name = person.AKA || `${person.f_name} ${person.l_name}`;
            const coord = await geocodeAdress(person.address.formattedAddress);

            return {
              id,
              name: name,
              address: person.address.formattedAddress,
              latitude: coord.latitude,
              longitude: coord.longitude
            };
          })
      );
      setPeopleLocations(locations);
    }
    fetchLocations();
  }, [peopleList, people]);

  useEffect(() => {
    if (!reunionList) return;

    async function fetchReunionLocations() {
      const locations = await Promise.all(
        reunionList
          .filter(([id, reunion]) => reunion.address && reunion.year > 1905)
          .map(async ([id, reunion]) => {
            const coord = await geocodeAdress(reunion.address.formattedAddress);

            return {
              id,
              name: `${reunion.year} Reunion`,
              address: reunion.address.formattedAddress,
              latitude: coord.latitude,
              longitude: coord.longitude
            };
          })
      );
      setReunionLocations(locations);
    }
    fetchReunionLocations();
  }, [reunionList, reunions]);

  const onSelectCity = useCallback(({longitude, latitude}) => {
    const curZoom = mapRef.current?.getZoom();
    const curLocation = mapRef.current?.getCenter();
    const map = mapRef.current?.getMap();
    const zoomDiff = Math.abs(curZoom - 9);
    const dist = Math.sqrt(Math.pow(longitude-curLocation.lng, 2) + Math.pow(latitude-curLocation.lat,2))
    const zoomTime = zoomDiff*60 + dist*90 + 700;

    map?.flyTo({
      center: [longitude, latitude], 
      zoom: 10, 
      duration:zoomTime,
      // duration: 2200,
      easing: t => (t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t)
    });
  }, []);

  const groupedLocations = useMemo(() => {
    if (!peopleLocations) return [];

    const NativeMap = window.Map
    const locationMap = new NativeMap();

    peopleLocations.forEach(person => {
      // Use address if available, otherwise coordinates
      // const key = person.address ??
      //   `${person.latitude.toFixed(6)}-${person.longitude.toFixed(6)}`;
      const key = `${person.latitude.toFixed(6)}-${person.longitude.toFixed(6)}`;

      if (!locationMap.has(key)) {
        locationMap.set(key, {
          ...person,
          count: 1,
          people: [person],
        });
      } else {
        const existing = locationMap.get(key);
        existing.count += 1;
        existing.people.push(person);
      }
    });

    return Array.from(locationMap.values());
  }, [peopleLocations]);

  const groupedReunions = useMemo(() => {
    if (!reunionLocations) return [];

    const NativeMap = window.Map
    const locationMap = new NativeMap();

    reunionLocations.forEach(reunion => {
      const key = `${reunion.latitude.toFixed(6)}-${reunion.longitude.toFixed(6)}`;

      if (!locationMap.has(key)) {
        locationMap.set(key, {
          ...reunion,
          count: 1,
          reunions: [reunion],
        });
      } else {
        const existing = locationMap.get(key);
        existing.count += 1;
        existing.reunions.push(reunion);
      }
    });

    return Array.from(locationMap.values());
  }, [reunionLocations]);

  const p_pins = useMemo(() => {
    // if (!groupedLocations) return [];
    return groupedLocations.map((city, index) => (
      <Marker
        key={`marker-${index}`}
        longitude={city.longitude}
        latitude={city.latitude}
        anchor='bottom'
        onClick={e => {
          e.originalEvent.stopPropagation();
          setPopupInfo(city)
          onSelectCity({longitude: city.longitude, latitude: city.latitude})
        }}
      >
        <Pin type={'person'} count={city.count}/>
      </Marker>
    ));
  }, [peopleLocations, onSelectCity]);

  const r_pins = useMemo(() => {
    return groupedReunions.map((city, index) => (
      <Marker
        key={`marker-${index}`}
        longitude={city.longitude}
        latitude={city.latitude}
        anchor='bottom'
        onClick={e => {
          e.originalEvent.stopPropagation();
          setPopupInfo(city)
          onSelectCity({longitude: city.longitude, latitude: city.latitude})
        }}
      >
        <Pin type={'reunion'} count={city.count}/>
      </Marker>
    ))
  }, [reunionLocations, onSelectCity]);

  const handleSearch = useCallback((query) => {
    const allLocations = [
      ...(peopleLocations || []),
      ...(reunionLocations || [])
    ];

    const found = allLocations.find((l) => 
      l.name.toLowerCase().includes(query.toLowerCase())
    );
    if (!found) return;

    onSelectCity({ longitude: found.longitude, latitude: found.latitude });
    setPopupInfo(found);
  }, [peopleLocations, reunionLocations, onSelectCity]);

  useEffect(() => {
    return () => {
      if (cleanupWheelRef.current) cleanupWheelRef.current();
    };
  }, []);

  const handleMapLoad = () => {
    setMapLoading(false);

    const mapContainer = mapRef.current?.getContainer();
    if (!mapContainer) return;

    const stopWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    mapContainer.addEventListener("wheel", stopWheel, { passive: false });
    
    cleanupWheelRef.current = () => {
      mapContainer.removeEventListener("wheel", stopWheel);
    };
  };

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.resize();
      }, 0)
    }
  }, [fullScreen])

  if (peopleLoading || reunionsLoading) return <Loading/>;
  if (peopleError || reunionsError) return <div>Error</div>;
  if (!peopleList || !reunionList) return <Loading/>;

  return (
    <div className={`map-page ${fullScreen?'full-screen':''}`}>
      <div className="page-title">Family Map</div>
      <div className={`map-container`}>
        {mapLoading && <Loading className='map-loading'/>}
        <Map
          ref={mapRef}
          {...mapState}
          onMove={e=>setMapState(e.mapState)}
          mapboxAccessToken={token}
          mapStyle={'mapbox://styles/mapbox/streets-v11'}
          // mapStyle={"mapbox://styles/mapbox/standard"}
          pitch={0}
          bearing={0}
          dragRotate={false}
          touchRotate={false}
          onLoad={handleMapLoad}
        > 
          <ControlPanel 
            p_isChecked={p_isChecked} 
            setP={setP}
            r_isChecked={r_isChecked}
            setR={setR}
          />
          <ZoomButtons mapRef={mapRef} initialState={initialState} />
          <SearchBar 
            onSearch={handleSearch} 
            data={[
              ...(peopleLocations || []), 
              ...(reunionLocations || [])
            ]} 
            mapRef={mapRef}
          />
          <FullScreen fullScreen={fullScreen} setFullScreen={setFullScreen}/>
          {p_isChecked && p_pins}
          {r_isChecked && r_pins}
          {popupInfo && (
            <Popup
              className='popup'
              anchor='top'
              longitude={Number(popupInfo.longitude)}
              latitude={Number(popupInfo.latitude)}
              onClose={() => setPopupInfo(null)}
            >
              <div>
                {peopleLocations
                  .filter(p => 
                    p.latitude === popupInfo.latitude && 
                    p.longitude === popupInfo.longitude
                  )
                  .sort((a, b) => {
                    const aNum = parseInt(a.id.substring(1), 10);
                    const bNum = parseInt(b.id.substring(1), 10);
                    return aNum - bNum;
                  })
                  .map(p => (
                    <div key={p.id}>
                      <Link to={`/people/${p.id}`}>{p.name}</Link>
                    </div>
                  ))
                }

                {reunionLocations
                  .filter(r =>
                    r.latitude === popupInfo.latitude &&
                    r.longitude === popupInfo.longitude
                  )
                  .map(r => (
                    <div key={r.id}>
                      <Link to={`/reunion/${r.id}`}>{r.name}</Link>
                    </div>
                  ))
                }

                <div className='address'>
                  {/* {popupInfo.address
                    .replace(/,\s*,/g, ",")  // remove empty sections like ", ,"
                    .replace(/\s+/g, " ")    // remove extra spaces
                    .trim()
                  } */}
                  {popupInfo.address
                    .replace(/\b(undefined|null)\b/g, "")
                    .split(",")
                    .map(part => part.trim())
                    .filter(Boolean)
                    .join(", ")
                  }
                </div>
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </div>
  );
}

async function geocodeAdress(address) {
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}`
  );

  const data = await response.json();
  const [longitude, latitude] = data.features[0].center;

  return {latitude, longitude};
}

const ICON = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;

const pinStyle_p = {
  cursor: 'pointer',
  fill: 'var(--blue7)',
  stroke: 'none'
};

const pinStyle_r = {
  cursor: 'pointer',
  fill: 'red',
  stroke: 'none'
};

function Pin({size = 21, type, count}) {
  const style = type === 'person' ? pinStyle_p : pinStyle_r;
  return (
    <svg 
      height={size} 
      viewBox="0 0 24 24" 
      style={{
        ...style,
        transform: 'translate(-50%, -100%)',
        position: 'absolute'
      }}
    >
      <path d={ICON} />
      {count > 1 && (
        <text
          x="12"
          y="11"
          textAnchor='middle'
          dominantBaseline='middle'
          fill='white'
          fontSize="11"
          fontWeight="bold"
        >
          {count}
        </text>
      )}
    </svg>
  );
}

function ControlPanel({p_isChecked, setP, r_isChecked, setR}) {
  return (
    <div className='control-panel'>
      <label>
        <input
          type='checkbox'
          className='p-check'
          checked={p_isChecked}
          onChange={(e) => setP(e.target.checked)}
        />
        <span>People</span>
      </label>
      <label>
        <input
          type='checkbox'
          className='r-check'
          checked={r_isChecked}
          onChange={(e) => setR(e.target.checked)}
        />
        <span>Reunions</span>
      </label>
    </div>
  );
}

function ZoomButtons ({mapRef, initialState}) {
  const zoomIn = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.zoomTo(map.getZoom()+1, {duration:500});
  }

  const zoomOut = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.zoomTo(map.getZoom()-1, {duration:500});
  }

  const zoomHome = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.flyTo({center: [initialState.longitude, initialState.latitude], zoom:initialState.zoom, duration:1000});
  }

  return (
    <div className='zoom-buttons'>
      <div>
        <button title='zoom in' className='in-button' onClick={zoomIn}>{'\u002B'}</button>
      </div>
      <div>
        <button title='zoom out' className='out-button' onClick={zoomOut}>{'\u2013'}</button>
      </div>
      <div>
        <button title='zoom home' className='home-button' onClick={zoomHome}>{'\u25C8'}</button>
      </div>
    </div>
  )
}

function SearchBar ({onSearch, data, mapRef}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const wrapperRef = useRef(null); 
  const popupRef = useRef(null);

  useEffect(() => {
    const map = mapRef.current;  
    if (!map) return;

    function handleMapClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.originalEvent.target)) {
        setSuggestions([]);
      }
    }

    map.on("click", handleMapClick);

    return () => {
      map.off("click", handleMapClick);  
    };
  }, [mapRef]);

  useEffect(() => {
    const popupEl = popupRef.current;
    if (!popupEl) return;

    function lockScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = popupEl;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;
 
      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
    }

    popupEl.addEventListener("wheel", lockScroll, { passive: false });

    return () => {
      popupEl.removeEventListener("wheel", lockScroll);
    };
  }, [suggestions]);
  
  function handleChange(e) {
    const value = e.target.value;
    setQuery(value);

    if (value.trim() === "") {
      setSuggestions([]);
      return
    }

    const matches = data
      .filter((p) => 
        p.name.toLowerCase().includes(value.toLowerCase())
      );
    setSuggestions(matches);
  }

  function handleKeyPress(e) {
    if (e.key === "Enter" && query.trim() !== "" && suggestions.length < 2) {
      onSearch(query.trim());
      setSuggestions([]);
    }
    if (e.key === "Escape") {
      setSuggestions([]);
      setQuery("");
      e.target.blur();
    }
  }

  function handleGo() {
    if (query.trim() !== "" && suggestions.length < 2) {
      onSearch(query.trim());
      setSuggestions([]);
    }
  }

  function handleSuggestionClick(person) {
    onSearch(person.name);
    setQuery(person.name);
    setSuggestions([]);
  }

  return (
    <div className='search-bar-wrapper' ref={wrapperRef}>
      {suggestions.length > 0 && (
        <div className='suggestions-popup' ref={popupRef}>
          {suggestions.map((p) => (
            <div
              key={p.id}
              className='suggestions-item'
              onClick={() => handleSuggestionClick(p)}
            >
              {p.name}
            </div>
          ))}
        </div>  
      )}
      <div className='search-bar'>
        <input 
          id='search-field'
          type='text'
          placeholder='search people or reunions'
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          autoComplete='off'
        />
        <button onClick={handleGo}>Go</button>
      </div>
    </div>
  )
}

function FullScreen ({fullScreen, setFullScreen}) {
  return (
    <div className={`fullscreen-button-map ${fullScreen?'in-fullscreen':''}`}>
      <button onClick={() => setFullScreen(!fullScreen)}>
        {!fullScreen && 
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M9 3H3v6" />
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M15 21h6v-6" />
          </svg>
        }
        {fullScreen &&
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M3 15h6v6" />
            <path d="M21 15h-6v6" />
            <path d="M3 9h6v-6" />
            <path d="M21 9h-6v-6" />
          </svg>
        }
      </button>
    </div>
  )
}